import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Cloud,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Save,
  Trash2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/datetime";

import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { useConfirm } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";

const TABS = [
  { id: "browser", label: "Browser engine" },
  { id: "email", label: "Email" },
];

export default function SettingsPage() {
  const [activeId, setActiveId] = useState(() => {
    if (typeof window === "undefined") return "browser";
    const hash = window.location.hash.replace(/^#/, "");
    return TABS.find((s) => s.id === hash) ? hash : "browser";
  });
  const [loading, setLoading] = useState(true);
  const [serverSettings, setServerSettings] = useState(null);

  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.replace(/^#/, "");
      if (TABS.find((s) => s.id === hash)) setActiveId(hash);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const selectTab = (id) => {
    setActiveId(id);
    if (typeof window !== "undefined" && window.history.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/platform-settings");
        if (!cancelled) setServerSettings(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !serverSettings) {
    return (
      <div
        className="max-w-7xl mx-auto px-4 sm:px-8 py-10 text-[#64748B] text-sm"
        data-testid="settings-loading"
      >
        Loading settings…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="settings-page">
      <PageHeader
        title="Platform Settings"
        description="Manage browser engine and email integration for the platform."
      />

      {/* Tabs */}
      <div className="border-b border-[#E5E7EB] mb-8" data-testid="settings-nav">
        <div className="flex gap-1">
          {TABS.map((t) => {
            const isActive = activeId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => selectTab(t.id)}
                data-testid={`settings-nav-${t.id}`}
                aria-current={isActive ? "page" : undefined}
                className={`relative px-4 py-3 text-sm font-medium transition-colors -mb-px border-b-2 ${
                  isActive
                    ? "text-[#3B82F6] border-[#3B82F6]"
                    : "text-[#64748B] border-transparent hover:text-[#0F172A]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div data-testid={`settings-section-${activeId}`}>
        {activeId === "browser" && (
          <BrowserEngineSection serverSettings={serverSettings} setServerSettings={setServerSettings} />
        )}
        {activeId === "email" && (
          <EmailSection serverSettings={serverSettings} setServerSettings={setServerSettings} />
        )}
      </div>
    </div>
  );
}

function InfoCard({ children, testId, className = "" }) {
  return (
    <div className={`surface-card p-5 ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

function BrowserEngineSection({ serverSettings, setServerSettings }) {
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [form, setForm] = useState({
    apiKeyInput: "",
    projectId: serverSettings.browserbase_project_id || "",
  });

  const isConfigured =
    serverSettings.browserbase_has_api_key &&
    !!serverSettings.browserbase_project_id;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: form.projectId.trim(),
      };
      if (form.apiKeyInput.trim()) payload.api_key = form.apiKeyInput.trim();
      const { data } = await api.put("/admin/platform-settings", payload);
      setServerSettings(data);
      setForm((f) => ({ ...f, apiKeyInput: "" }));
      setShowKey(false);
      toast.success(
        data.browserbase_enabled
          ? "Browserbase saved — recordings and runs are ready to go."
          : "Saved. Add both an API key and Project ID to activate Browserbase."
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!form.apiKeyInput.trim() && !serverSettings.browserbase_has_api_key) {
      toast.error("Enter an API key first.");
      return;
    }
    if (!form.projectId.trim()) {
      toast.error("Project ID is required.");
      return;
    }
    if (!form.apiKeyInput.trim()) {
      toast.info("Re-enter your API key here to run a connection test (the stored key is encrypted and can't be read back).");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/platform-settings/browserbase/test", {
        api_key: form.apiKeyInput.trim(),
        project_id: form.projectId.trim(),
      });
      toast.success(`Connected to Browserbase. Session id ${data.session_id.slice(0, 8)}…`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const handleClearKey = async () => {
    const ok = await confirm({
      title: "Remove the saved Browserbase API key?",
      description:
        "Probe runs only on Browserbase. Removing the key will stop ALL recordings and test runs until a new key is saved.",
      confirmLabel: "Remove key",
      danger: true,
      testId: "confirm-clear-browserbase-key",
    });
    if (!ok) return;
    setClearing(true);
    try {
      const { data } = await api.delete("/admin/platform-settings/browserbase-key");
      setServerSettings(data);
      setForm((f) => ({ ...f, apiKeyInput: "" }));
      toast.success("Browserbase key removed. Recordings and runs are paused until a new key is saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Status banner — green if configured, red if not (no Local Chromium fallback) */}
      <InfoCard
        testId="browser-engine-status"
        className={
          isConfigured
            ? "border-l-4 border-l-[#10B981]"
            : "border-l-4 border-l-[#EF4444] bg-[#FEF2F2]"
        }
      >
        <div className="flex items-start gap-4">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${
              isConfigured
                ? "bg-[#10B981]/10 text-[#10B981]"
                : "bg-[#EF4444]/10 text-[#EF4444]"
            }`}
          >
            {isConfigured ? <Cloud size={18} /> : <AlertTriangle size={18} />}
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-[#64748B]">Browser engine</p>
            <p
              className="font-['Geist'] font-semibold text-lg mt-0.5 text-[#0F172A]"
              data-testid="browser-engine-label"
            >
              {isConfigured
                ? "Browserbase — ready"
                : "Browserbase — not configured"}
            </p>
            <p className="text-sm text-[#64748B] mt-1 leading-relaxed">
              {isConfigured
                ? "Recordings and test runs connect to a Browserbase Chromium session over CDP."
                : "Probe runs exclusively on Browserbase. Save an API key and Project ID below to enable recordings and test runs."}
            </p>
            {!isConfigured && (
              <p className="text-xs text-[#B91C1C] mt-2 font-medium" data-testid="browser-engine-warning">
                ⚠ All recordings and test runs will fail until configuration is complete.
              </p>
            )}
          </div>
        </div>
      </InfoCard>

      {/* Stored Configuration card */}
      <InfoCard testId="browserbase-saved-state">
        <div className="flex items-start gap-3 mb-3">
          {serverSettings.browserbase_has_api_key ? (
            <CheckCircle2 size={16} className="text-[#10B981] mt-0.5 shrink-0" />
          ) : (
            <AlertTriangle size={16} className="text-[#F59E0B] mt-0.5 shrink-0" />
          )}
          <div className="flex-1">
            <p className="text-sm font-medium text-[#0F172A]">Stored Configuration</p>
            <p className="text-xs text-[#64748B]" data-testid="browserbase-saved-state-line">
              {serverSettings.browserbase_has_api_key
                ? `API key ${serverSettings.browserbase_api_key_masked} · Project ${serverSettings.browserbase_project_id || "—"}`
                : "Not configured yet"}
            </p>
            {serverSettings.updated_at && (
              <p className="text-xs text-[#94A3B8] mt-1">
                Last updated {formatDateTime(serverSettings.updated_at)}
                {serverSettings.updated_by && ` by ${serverSettings.updated_by}`}
              </p>
            )}
          </div>
        </div>
      </InfoCard>

      {/* Configuration form */}
      <InfoCard testId="browserbase-config-card">
        <div className="mb-5">
          <h3 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">Browserbase</h3>
          <p className="text-sm text-[#64748B]">
            Required remote browser engine — paste your API key + Project ID below.
          </p>
        </div>

        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-[#475569] block mb-1.5">API key</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showKey ? "text" : "password"}
                  value={form.apiKeyInput}
                  onChange={(e) => setForm((f) => ({ ...f, apiKeyInput: e.target.value }))}
                  placeholder={
                    serverSettings.browserbase_has_api_key
                      ? `Stored: ${serverSettings.browserbase_api_key_masked} — leave blank to keep`
                      : "Paste your Browserbase API key (starts with bb_live_)"
                  }
                  data-testid="browserbase-api-key-input"
                  className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6] font-mono text-xs pr-9"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  data-testid="toggle-api-key-visibility"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                  aria-label="Toggle API key visibility"
                >
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {serverSettings.browserbase_has_api_key && (
                <button
                  onClick={handleClearKey}
                  disabled={clearing}
                  data-testid="clear-api-key-button"
                  className="flex items-center gap-1.5 h-10 px-3 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white text-[#B91C1C] hover:bg-[#FEF2F2] disabled:opacity-60"
                  title="Remove saved key"
                >
                  {clearing ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Remove
                </button>
              )}
            </div>
            <p className="text-xs text-[#64748B] mt-1.5">
              Grab a key from{" "}
              <a href="https://www.browserbase.com/settings" target="_blank" rel="noreferrer" className="text-[#3B82F6] hover:underline">
                browserbase.com/settings
              </a>
              . Stored encrypted, never exposed in API responses.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-[#475569] block mb-1.5">Project ID</label>
            <Input
              value={form.projectId}
              onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}
              placeholder="proj_xxxxxxxxxxxx"
              data-testid="browserbase-project-id-input"
              className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6] font-mono text-xs"
              autoComplete="off"
            />
            <p className="text-xs text-[#64748B] mt-1.5">
              Visible in the Browserbase dashboard header next to your account name.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 pt-4 border-t border-[#F1F5F9]">
            <button
              onClick={handleTest}
              disabled={testing || !form.apiKeyInput.trim() || !form.projectId.trim()}
              data-testid="test-connection-button"
              className="flex items-center gap-1.5 h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white text-[#0F172A] hover:bg-[#F8FAFC] disabled:opacity-60"
            >
              {testing ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
              Test connection
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings-button"
              className="flex items-center gap-1.5 h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 ml-auto"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save settings
            </button>
          </div>
        </div>
      </InfoCard>
    </div>
  );
}

function EmailSection({ serverSettings, setServerSettings }) {
  const confirm = useConfirm();
  const [showKey, setShowKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [clearingKey, setClearingKey] = useState(false);
  const [savingSender, setSavingSender] = useState(false);
  const [senderForm, setSenderForm] = useState({
    from_first_name: serverSettings.email_from_first_name || "",
    from_last_name: serverSettings.email_from_last_name || "",
    from_email: serverSettings.email_from_email || "",
  });

  const senderDisplay = (() => {
    const name = [senderForm.from_first_name, senderForm.from_last_name].filter(Boolean).join(" ").trim();
    const email = senderForm.from_email || "noreply@example.com";
    return name ? `${name} <${email}>` : email;
  })();

  const handleSaveKey = async () => {
    const trimmed = apiKeyInput.trim();
    if (!trimmed) {
      toast.error("Enter an API key first");
      return;
    }
    if (!trimmed.startsWith("re_")) {
      toast.error("Resend keys start with `re_`");
      return;
    }
    setSavingKey(true);
    try {
      const { data } = await api.put("/admin/platform-settings/email", { api_key: trimmed });
      setServerSettings(data);
      setApiKeyInput("");
      setShowKey(false);
      toast.success("Resend API key saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingKey(false);
    }
  };

  const handleClearKey = async () => {
    const ok = await confirm({
      title: "Remove the saved Resend API key?",
      description: "Outbound emails will fail until a new key is saved.",
      confirmLabel: "Remove key",
      danger: true,
      testId: "confirm-clear-email-key",
    });
    if (!ok) return;
    setClearingKey(true);
    try {
      const { data } = await api.delete("/admin/platform-settings/email-key");
      setServerSettings(data);
      toast.success("Resend API key removed.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClearingKey(false);
    }
  };

  const handleSaveSender = async () => {
    setSavingSender(true);
    try {
      const { data } = await api.put("/admin/platform-settings/email", {
        from_first_name: senderForm.from_first_name,
        from_last_name: senderForm.from_last_name,
        from_email: senderForm.from_email,
      });
      setServerSettings(data);
      toast.success("Sender information saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingSender(false);
    }
  };

  const configured = serverSettings.email_has_api_key;

  return (
    <div className="space-y-6 max-w-3xl">
      <InfoCard testId="email-config-card">
        <div className="flex items-start gap-3 mb-5">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] shrink-0">
            <Mail size={18} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-['Geist'] font-semibold text-lg text-[#0F172A]">Resend</p>
              {configured ? (
                <span
                  data-testid="email-configured-badge"
                  className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border border-[#10B981]/20 bg-[#10B981]/10 text-[#047857]"
                >
                  <CheckCircle2 size={11} /> Configured
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border border-[#F59E0B]/20 bg-[#F59E0B]/10 text-[#B45309]">
                  <AlertTriangle size={11} /> Not configured
                </span>
              )}
            </div>
            <p className="text-sm text-[#64748B]">Transactional emails — invitations, password resets, alerts.</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-[#475569] block mb-1.5">Resend API key</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showKey ? "text" : "password"}
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder={
                    configured
                      ? `Stored: ${serverSettings.email_api_key_masked} — leave blank to keep`
                      : "Paste your Resend API key (starts with re_)"
                  }
                  data-testid="email-api-key-input"
                  className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6] font-mono text-xs pr-9"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  data-testid="toggle-email-api-key-visibility"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                  aria-label="Toggle key visibility"
                >
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {configured && (
                <button
                  onClick={handleClearKey}
                  disabled={clearingKey}
                  data-testid="clear-email-key-button"
                  className="flex items-center gap-1.5 h-10 px-3 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white text-[#B91C1C] hover:bg-[#FEF2F2] disabled:opacity-60"
                >
                  {clearingKey ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Remove
                </button>
              )}
            </div>
            <p className="text-xs text-[#64748B] mt-1.5">
              Get your API key from{" "}
              <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#3B82F6] hover:underline">
                resend.com/api-keys
              </a>
              .
            </p>
            <div className="mt-3">
              <button
                onClick={handleSaveKey}
                disabled={savingKey || !apiKeyInput.trim()}
                data-testid="save-email-key-button"
                className="flex items-center gap-1.5 h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60"
              >
                {savingKey ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {configured ? "Update API key" : "Save API key"}
              </button>
            </div>
          </div>

          <div className="border-t border-[#F1F5F9] pt-5">
            <p className="text-sm font-medium text-[#475569] mb-4">Sender information</p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#475569] block mb-1.5">First name</label>
                <Input
                  value={senderForm.from_first_name}
                  onChange={(e) => setSenderForm({ ...senderForm, from_first_name: e.target.value })}
                  data-testid="email-from-first-name-input"
                  className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#475569] block mb-1.5">Last name</label>
                <Input
                  value={senderForm.from_last_name}
                  onChange={(e) => setSenderForm({ ...senderForm, from_last_name: e.target.value })}
                  data-testid="email-from-last-name-input"
                  className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-sm font-medium text-[#475569] block mb-1.5">Sender email</label>
              <Input
                type="email"
                value={senderForm.from_email}
                onChange={(e) => setSenderForm({ ...senderForm, from_email: e.target.value })}
                placeholder="noreply@helloprobe.com"
                data-testid="email-from-email-input"
                className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6] font-mono text-xs"
                autoComplete="off"
              />
              <p className="text-xs text-[#64748B] mt-1.5">
                Use <span className="font-mono">onboarding@resend.dev</span> for testing, or your verified domain email for production.
              </p>
            </div>

            <div data-testid="email-sender-preview" className="mt-4 border border-[#E5E7EB] bg-[#F8FAFC] rounded-md p-3">
              <p className="text-xs text-[#64748B] mb-1">Emails will be sent as:</p>
              <p className="font-mono text-sm text-[#0F172A]">{senderDisplay}</p>
            </div>

            <div className="mt-4">
              <button
                onClick={handleSaveSender}
                disabled={savingSender}
                data-testid="save-email-sender-button"
                className="flex items-center gap-1.5 h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white text-[#0F172A] hover:bg-[#F8FAFC] disabled:opacity-60"
              >
                {savingSender ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Save sender information
              </button>
            </div>

            {serverSettings.email_updated_at && (
              <p className="text-xs text-[#94A3B8] mt-3">
                Last updated {formatDateTime(serverSettings.email_updated_at)}
                {serverSettings.email_updated_by && ` by ${serverSettings.email_updated_by}`}
              </p>
            )}
          </div>
        </div>
      </InfoCard>
    </div>
  );
}
